import React, { useMemo, useState } from 'react';

import { Autocomplete, CircularProgress, TextField } from '@mui/material';

import { SEARCH_DEBOUNCE_MS } from '~/consts/tours';

import { useSearchFlashOffers } from '~/hooks/apis/offers/useSearchFlashOffers';
import useDebounced from '~/hooks/useDebounced';

interface Props {
  value: App.Offer | null;
  onChange: (offer: App.Offer | null) => void;
}

export default function FlashOfferPicker({ value, onChange }: Props) {
  const [inputValue, setInputValue] = useState('');

  const search = useDebounced(inputValue.trim(), SEARCH_DEBOUNCE_MS);

  const { data: options = [], isFetching } = useSearchFlashOffers(search);

  // Kept in the list so the field does not clear itself as the search changes.
  const mergedOptions = useMemo(
    () =>
      !value || options.some((option) => option.id_salesforce_external === value.id_salesforce_external)
        ? options
        : [value, ...options],
    [options, value],
  );

  return (
    <Autocomplete
      value={value}
      onChange={(_event, offer) => onChange(offer)}
      inputValue={inputValue}
      onInputChange={(_event, next) => setInputValue(next)}
      options={mergedOptions}
      getOptionLabel={(option) => `${option.name} (${option.id_salesforce_external})`}
      isOptionEqualToValue={(option, selected) => option.id_salesforce_external === selected.id_salesforce_external}
      filterOptions={(all) => all}
      loading={isFetching}
      noOptionsText={search ? `No flash hotels match "${search}"` : 'No flash hotels found'}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Flash hotel"
          helperText="Search by name or offer id"
          required
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
